import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Map as MapIcon, Flame, MapPin, Loader2, Users } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ── CDN loaders ─────────────────────────────────────────────────────────────

const LEAFLET_CSS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
const LEAFLET_JS  = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
const CLUSTER_CSS = 'https://unpkg.com/leaflet.markercluster@1.5.3/dist/MarkerCluster.css';
const CLUSTER_DEFAULT_CSS = 'https://unpkg.com/leaflet.markercluster@1.5.3/dist/MarkerCluster.Default.css';
const CLUSTER_JS  = 'https://unpkg.com/leaflet.markercluster@1.5.3/dist/leaflet.markercluster.js';
const HEAT_JS     = 'https://unpkg.com/leaflet.heat@0.2.0/dist/leaflet-heat.js';

const loadCSS = (href) => new Promise((resolve) => {
  if (document.querySelector(`link[href="${href}"]`)) return resolve();
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = href;
  link.onload = resolve;
  link.onerror = resolve;
  document.head.appendChild(link);
});

const loadScript = (src) => new Promise((resolve, reject) => {
  if (document.querySelector(`script[src="${src}"]`)) return resolve();
  const s = document.createElement('script');
  s.src = src;
  s.async = true;
  s.onload = resolve;
  s.onerror = reject;
  document.head.appendChild(s);
});

const loadLeafletAll = async () => {
  await Promise.all([loadCSS(LEAFLET_CSS), loadCSS(CLUSTER_CSS), loadCSS(CLUSTER_DEFAULT_CSS)]);
  await loadScript(LEAFLET_JS);
  await Promise.all([loadScript(CLUSTER_JS), loadScript(HEAT_JS)]);
};

// ── Postcode helpers ────────────────────────────────────────────────────────

const normalisePostcode = (pc) => (pc || '').replace(/\s+/g, '').toUpperCase();

const postcodeArea = (pc) => {
  const m = (pc || '').toUpperCase().match(/^([A-Z]{1,2})/);
  return m ? m[1] : '';
};

// Bulk geocode via postcodes.io (max 100 per request)
const bulkGeocode = async (postcodes) => {
  const results = {};
  const chunks = [];
  for (let i = 0; i < postcodes.length; i += 100) chunks.push(postcodes.slice(i, i + 100));

  for (const chunk of chunks) {
    try {
      const res = await fetch('https://api.postcodes.io/postcodes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ postcodes: chunk }),
      });
      if (!res.ok) continue;
      const data = await res.json();
      (data.result || []).forEach(item => {
        if (item.result) {
          results[normalisePostcode(item.query)] = {
            lat: item.result.latitude,
            lng: item.result.longitude,
            postcode: item.result.postcode,
          };
        }
      });
    } catch (e) {
      console.error('Geocode chunk failed', e);
    }
  }
  return results;
};

// ── Component ───────────────────────────────────────────────────────────────

const HeatMap = () => {
  const navigate = useNavigate();
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const clusterLayer = useRef(null);
  const heatLayer = useRef(null);

  const [clients, setClients] = useState([]);
  const [cases, setCases] = useState([]);
  const [coords, setCoords] = useState({}); // normalised pc -> {lat, lng, postcode}
  const [loading, setLoading] = useState(true);
  const [geocoding, setGeocoding] = useState(false);
  const [filter, setFilter] = useState('all'); // all / mortgage / insurance
  const [showHeat, setShowHeat] = useState(false);

  // ── Fetch data ────────────────────────────────────────────────────────────

  useEffect(() => {
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${localStorage.getItem('token')}`,
    };
    (async () => {
      try {
        const [cr, csr] = await Promise.all([
          fetch(`${API_URL}/api/clients?limit=2000`, { headers }),
          fetch(`${API_URL}/api/cases?limit=2000`,   { headers }),
        ]);
        const cd  = cr.ok  ? await cr.json()  : {};
        const csd = csr.ok ? await csr.json() : {};
        setClients(cd.clients || []);
        setCases(csd.cases || []);
      } catch (e) {
        console.error('Failed to load data', e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // ── Geocode postcodes ─────────────────────────────────────────────────────

  useEffect(() => {
    if (!clients.length) return;
    const uniquePostcodes = Array.from(new Set(
      clients.map(c => normalisePostcode(c.postcode)).filter(Boolean)
    ));
    if (!uniquePostcodes.length) return;

    setGeocoding(true);
    bulkGeocode(uniquePostcodes).then(res => {
      setCoords(res);
      setGeocoding(false);
    });
  }, [clients]);

  // ── Build per-client data with case counts and product types ──────────────

  const caseCountByClient = useMemo(() => {
    const map = {};
    cases.forEach(c => {
      if (!c.client_id) return;
      if (!map[c.client_id]) map[c.client_id] = { total: 0, mortgage: 0, insurance: 0 };
      map[c.client_id].total += 1;
      if (c.product_type === 'mortgage')  map[c.client_id].mortgage  += 1;
      if (c.product_type === 'insurance') map[c.client_id].insurance += 1;
    });
    return map;
  }, [cases]);

  // Clients with valid coords AND match filter
  const mappedClients = useMemo(() => {
    return clients
      .map(c => {
        const key = normalisePostcode(c.postcode);
        const co = coords[key];
        if (!co) return null;
        const caseInfo = caseCountByClient[c.client_id] || { total: 0, mortgage: 0, insurance: 0 };
        return { ...c, lat: co.lat, lng: co.lng, _pcDisplay: co.postcode, caseInfo };
      })
      .filter(c => c !== null)
      .filter(c => {
        if (filter === 'all') return true;
        if (filter === 'mortgage')  return c.caseInfo.mortgage  > 0;
        if (filter === 'insurance') return c.caseInfo.insurance > 0;
        return true;
      });
  }, [clients, coords, caseCountByClient, filter]);

  // ── Stats ─────────────────────────────────────────────────────────────────

  const stats = useMemo(() => {
    // top postcodes (by individual postcode like "LE3 4AB" within mapped set)
    const pcCounts = {};
    mappedClients.forEach(c => {
      const k = c._pcDisplay || normalisePostcode(c.postcode);
      pcCounts[k] = (pcCounts[k] || 0) + 1;
    });
    const topPostcodes = Object.entries(pcCounts).sort((a, b) => b[1] - a[1]).slice(0, 10);

    const areaCounts = {};
    mappedClients.forEach(c => {
      const a = postcodeArea(c._pcDisplay || c.postcode);
      if (!a) return;
      areaCounts[a] = (areaCounts[a] || 0) + 1;
    });
    const topAreas = Object.entries(areaCounts).sort((a, b) => b[1] - a[1]).slice(0, 5);

    return {
      topPostcodes,
      topAreas,
      totalMapped: mappedClients.length,
      totalClients: clients.length,
      withoutPostcode: clients.filter(c => !c.postcode || !c.postcode.trim()).length,
    };
  }, [mappedClients, clients]);

  // ── Init Leaflet map ──────────────────────────────────────────────────────

  useEffect(() => {
    let cancelled = false;
    loadLeafletAll().then(() => {
      if (cancelled || !mapRef.current || mapInstance.current) return;
      const L = window.L;
      const map = L.map(mapRef.current, {
        center: [51.5074, -0.1278], // London default
        zoom: 6,
        scrollWheelZoom: true,
      });
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors',
        maxZoom: 19,
      }).addTo(map);
      mapInstance.current = map;
      // Trigger a redraw of layers once map is ready
      setCoords(c => ({ ...c }));
    }).catch(e => console.error('Leaflet load failed', e));
    return () => { cancelled = true; };
  }, []);

  // ── Render markers / heat ─────────────────────────────────────────────────

  const renderLayers = useCallback(() => {
    const L = window.L;
    const map = mapInstance.current;
    if (!L || !map) return;

    // Clear existing layers
    if (clusterLayer.current) {
      map.removeLayer(clusterLayer.current);
      clusterLayer.current = null;
    }
    if (heatLayer.current) {
      map.removeLayer(heatLayer.current);
      heatLayer.current = null;
    }

    if (!mappedClients.length) return;

    // Custom red marker icon (brand red-600 = #DC2626)
    const redIcon = L.divIcon({
      html: `<div style="
        background:#DC2626;
        width:18px;
        height:18px;
        border-radius:50% 50% 50% 0;
        transform:rotate(-45deg);
        border:2px solid #fff;
        box-shadow:0 1px 4px rgba(0,0,0,0.4);
      "></div>`,
      className: '',
      iconSize: [22, 22],
      iconAnchor: [11, 22],
    });

    if (showHeat) {
      const points = mappedClients.map(c => [c.lat, c.lng, 0.6]);
      heatLayer.current = L.heatLayer(points, {
        radius: 28,
        blur: 22,
        maxZoom: 12,
        gradient: { 0.2: '#3b82f6', 0.4: '#10b981', 0.6: '#f59e0b', 0.8: '#dc2626', 1.0: '#7f1d1d' },
      }).addTo(map);
    } else {
      const cluster = L.markerClusterGroup({
        showCoverageOnHover: false,
        spiderfyOnMaxZoom: true,
        iconCreateFunction: (c) => L.divIcon({
          html: `<div style="
            background:#DC2626;
            color:#fff;
            width:38px;
            height:38px;
            border-radius:50%;
            display:flex;
            align-items:center;
            justify-content:center;
            font-weight:700;
            font-size:13px;
            border:3px solid #fff;
            box-shadow:0 2px 6px rgba(0,0,0,0.4);
          ">${c.getChildCount()}</div>`,
          className: '',
          iconSize: [44, 44],
        }),
      });
      mappedClients.forEach(c => {
        const marker = L.marker([c.lat, c.lng], { icon: redIcon });
        const escName = `${c.first_name || ''} ${c.last_name || ''}`.trim().replace(/[<>]/g, '');
        const html = `
          <div style="font-family:system-ui,sans-serif;min-width:160px">
            <p style="font-weight:600;margin:0 0 4px;font-size:14px">${escName}</p>
            <p style="margin:0 0 2px;font-size:12px;color:#64748b">${c._pcDisplay || ''}</p>
            <p style="margin:0 0 6px;font-size:12px;color:#64748b">${c.caseInfo.total} case${c.caseInfo.total === 1 ? '' : 's'}</p>
            <a href="#" data-client-id="${c.client_id}" class="hm-client-link" style="
              color:#dc2626;font-weight:600;font-size:12px;text-decoration:none;
            ">View client →</a>
          </div>
        `;
        marker.bindPopup(html);
        cluster.addLayer(marker);
      });
      cluster.addTo(map);
      clusterLayer.current = cluster;
    }

    // Auto-fit
    if (mappedClients.length > 0) {
      const bounds = L.latLngBounds(mappedClients.map(c => [c.lat, c.lng]));
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 13 });
    }
  }, [mappedClients, showHeat]);

  useEffect(() => { renderLayers(); }, [renderLayers]);

  // Delegate popup link clicks → React navigate
  useEffect(() => {
    const handler = (e) => {
      const link = e.target.closest('.hm-client-link');
      if (!link) return;
      e.preventDefault();
      const id = link.getAttribute('data-client-id');
      if (id) navigate(`/clients/${id}`);
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [navigate]);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="p-6 space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: 'Plus Jakarta Sans' }}>
            Postcode Heat Map
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">Geographic distribution of your client base.</p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* Filter pills */}
          <div className="flex rounded-lg border border-slate-400 dark:border-slate-500 overflow-hidden">
            {[
              { key: 'all',       label: 'All' },
              { key: 'mortgage',  label: 'Mortgage' },
              { key: 'insurance', label: 'Insurance' },
            ].map((opt, i) => (
              <button
                key={opt.key}
                onClick={() => setFilter(opt.key)}
                className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                  filter === opt.key
                    ? 'bg-red-600 text-white'
                    : 'bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-600'
                } ${i > 0 ? 'border-l border-slate-400 dark:border-slate-500' : ''}`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {/* Heat map toggle */}
          <Button
            onClick={() => setShowHeat(s => !s)}
            className={showHeat ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-600'}
          >
            <Flame className="h-4 w-4 mr-2" />
            {showHeat ? 'Heat Map On' : 'Heat Map Off'}
          </Button>
        </div>
      </div>

      {/* Map + Stats grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Map */}
        <Card className="lg:col-span-2 border-slate-200 dark:border-slate-700 dark:bg-slate-800 overflow-hidden">
          <CardContent className="p-0 relative">
            {(loading || geocoding) && (
              <div className="absolute inset-0 z-[1000] bg-white/80 dark:bg-slate-900/80 flex items-center justify-center">
                <div className="text-center">
                  <Loader2 className="h-8 w-8 animate-spin text-red-600 mx-auto mb-2" />
                  <p className="text-sm text-slate-600 dark:text-slate-300">
                    {loading ? 'Loading clients...' : 'Geocoding postcodes...'}
                  </p>
                </div>
              </div>
            )}
            <div ref={mapRef} style={{ height: '650px', width: '100%' }} className="bg-slate-100 dark:bg-slate-900" />
          </CardContent>
        </Card>

        {/* Stats panel */}
        <div className="space-y-4">
          {/* Summary */}
          <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                <Users className="h-4 w-4 text-red-600" />
                Coverage
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 pt-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-500 dark:text-slate-400">Mapped clients</span>
                <span className="text-xl font-bold text-slate-900 dark:text-white">
                  {stats.totalMapped}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-500 dark:text-slate-400">Total clients</span>
                <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  {stats.totalClients}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-500 dark:text-slate-400">Missing postcode</span>
                <span className="text-sm font-medium text-amber-600 dark:text-amber-400">
                  {stats.withoutPostcode}
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Top areas */}
          <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                <MapIcon className="h-4 w-4 text-red-600" />
                Top Areas
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-2">
              {stats.topAreas.length === 0 ? (
                <p className="text-sm text-slate-400 italic">No data</p>
              ) : (
                <div className="space-y-2">
                  {stats.topAreas.map(([area, count]) => {
                    const max = stats.topAreas[0][1];
                    const pct = Math.round((count / max) * 100);
                    return (
                      <div key={area}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-mono font-semibold text-slate-700 dark:text-slate-200">{area}</span>
                          <span className="text-xs text-slate-500 dark:text-slate-400">{count} clients</span>
                        </div>
                        <div className="h-1.5 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                          <div className="h-full bg-red-600 rounded-full transition-all" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Top postcodes */}
          <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                <MapPin className="h-4 w-4 text-red-600" />
                Top Postcodes
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-2">
              {stats.topPostcodes.length === 0 ? (
                <p className="text-sm text-slate-400 italic">No data</p>
              ) : (
                <div className="space-y-1.5">
                  {stats.topPostcodes.map(([pc, count], i) => (
                    <div key={pc} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-xs text-slate-400 dark:text-slate-500 w-4">{i + 1}.</span>
                        <span className="font-mono text-slate-700 dark:text-slate-200 truncate">{pc}</span>
                      </div>
                      <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 shrink-0 ml-2">
                        {count}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default HeatMap;
